import { Link } from 'react-router-dom';
import {
  ArrowRight, Phone, CheckCircle2, Sparkles, TrendingUp, Users, Search, Target,
  PenTool, BarChart3, Megaphone, Globe, Mail, Award, Quote, Star, ExternalLink,
} from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import {
  HEADLINE_STATS, PARTNERSHIP_PILLARS, SERVICE_TABS, AUDIENCE_TIERS,
  PROCESS_STEPS, TESTIMONIALS, RESULT_CASES, CLIENT_BRANDS,
} from '@/lib/content';
import { PORTFOLIO } from '@/lib/portfolio';
import { BLOGS } from '@/lib/blogs';
import { useMemo, useState } from 'react';

const SERVICE_ICONS = {
  marketing: <Megaphone size={22} />,
  web: <Globe size={22} />,
  creative: <PenTool size={22} />,
  growth: <BarChart3 size={22} />,
};

const COLOR_MAP = {
  blue: { bg: 'bg-bp-blue-50', text: 'text-bp-blue', border: 'border-bp-blue', soft: '#E6F2FE', solid: '#0072EE' },
  teal: { bg: 'bg-bp-teal-50', text: 'text-bp-teal', border: 'border-bp-teal', soft: '#E8FBF8', solid: '#14B8A6' },
  amber: { bg: 'bg-bp-amber-50', text: 'text-bp-amber', border: 'border-bp-amber', soft: '#FEF6E6', solid: '#F59E0B' },
  coral: { bg: 'bg-orange-50', text: 'text-bp-coral', border: 'border-bp-coral', soft: '#FFF1EC', solid: '#FB6A48' },
};

export default function HomePage() {
  const [activeTab, setActiveTab] = useState('marketing');
  const current = SERVICE_TABS.find((t) => t.key === activeTab);

  // 3 most recent portfolio launches by addedAt
  const freshLaunches = useMemo(() => {
    return PORTFOLIO
      .filter((p) => p.addedAt)
      .sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime())
      .slice(0, 3);
  }, []);

  // 3 most recent blog posts by date string
  const freshArticles = useMemo(() => {
    return [...BLOGS]
      .sort((a, b) => {
        const ta = a.date ? Date.parse(a.date) : 0;
        const tb = b.date ? Date.parse(b.date) : 0;
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      })
      .slice(0, 3);
  }, []);

  return (
    <main data-testid="home-page" className="bg-white">
      <SEO
        title="Real Partners. Real Growth. Real Results."
        description="Florida's digital marketing partner since 2007. SEO, PPC, web design, social - built on partnership, measured in results. For local heroes and growing brands alike."
        path="/"
      />

      {/* ─────────────────── HERO ─────────────────── */}
      <section className="relative pt-12 lg:pt-20 pb-20 lg:pb-28 bg-radial-blue overflow-hidden" data-testid="hero">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none" />
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal as="div" className="inline-flex items-center gap-2 bg-white border border-bp-slate-200 rounded-full px-3.5 py-1.5 shadow-sm">
                <Award size={14} className="text-bp-amber" />
                <span className="text-[12.5px] font-semibold text-bp-slate">Voted Top 25 Agency · South Florida Business Journal</span>
              </Reveal>

              <Reveal as="h1" delay={80} className="h-display mt-6 text-[44px] sm:text-[64px] lg:text-[80px]">
                Real partners.<br/>
                <span className="grad-text">Real growth.</span><br/>
                Real results.
              </Reveal>

              <Reveal as="p" delay={160} className="mt-6 text-[18px] leading-relaxed text-bp-slate max-w-xl">
                We&rsquo;re the marketing partner businesses keep for 10+ years - strategy, design, SEO, ads,
                and content under one roof. Built for the corner shop. Trusted by the enterprise.
              </Reveal>

              <Reveal as="div" delay={220} className="mt-9 flex flex-wrap items-center gap-3">
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
                  <Phone size={16} /> Book a Free Strategy Call
                </Link>
                <Link to="/services" className="btn-secondary" data-testid="hero-cta-secondary">
                  Explore Services <ArrowRight size={16} />
                </Link>
              </Reveal>

              <Reveal as="div" delay={300} className="mt-10 flex items-center gap-6 flex-wrap text-[13.5px]">
                <div className="flex items-center gap-2 text-bp-slate"><CheckCircle2 size={16} className="text-bp-teal"/> No long-term contracts</div>
                <div className="flex items-center gap-2 text-bp-slate"><CheckCircle2 size={16} className="text-bp-teal"/> Free audit, no pressure</div>
                <div className="flex items-center gap-2 text-bp-slate"><CheckCircle2 size={16} className="text-bp-teal"/> 24h response</div>
              </Reveal>
            </div>

            {/* Hero visual - partnership card stack */}
            <Reveal as="div" delay={150} className="lg:col-span-5 relative">
              <div className="relative aspect-[5/6] max-w-[460px] mx-auto">
                {/* Back card - "Your Brand" */}
                <div className="absolute top-0 left-0 right-8 bottom-12 card-floating p-6 rotate-[-3deg]" style={{background:'linear-gradient(135deg,#fff 0%,#FEF6E6 100%)'}}>
                  <div className="kicker"><span className="dot" style={{background:'#F59E0B'}}/>Your Brand</div>
                  <div className="font-display font-bold text-[26px] text-bp-ink leading-tight mt-3">
                    Built for trust, ready to grow.
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-bp-amber/15 flex items-center justify-center"><Users size={18} className="text-bp-amber"/></div>
                    <div className="text-[13px] text-bp-slate-soft">Local · Regional · Enterprise</div>
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-2">
                    {['SEO', 'Web', 'Ads'].map((t)=>(<div key={t} className="text-center py-2 rounded-lg bg-white border border-bp-slate-200 text-[12px] font-semibold text-bp-slate">{t}</div>))}
                  </div>
                </div>

                {/* Front card - "Our Team" */}
                <div className="absolute bottom-0 right-0 left-8 top-12 card-floating p-6 rotate-[3deg] z-10">
                  <div className="flex items-center justify-between">
                    <div className="kicker"><span className="dot"/>BrandPartners</div>
                    <div className="flex -space-x-2">
                      {['#0072EE','#14B8A6','#F59E0B','#FB6A48'].map((c,i)=>(<div key={i} className="w-7 h-7 rounded-full ring-2 ring-white" style={{background:c}}/>))}
                    </div>
                  </div>
                  <div className="font-display font-bold text-[26px] text-bp-ink leading-tight mt-3">
                    Strategy + execution, in one room.
                  </div>
                  <div className="mt-5 space-y-2.5">
                    {[
                      { label: 'Qualified leads', value: '+312%', color: '#14B8A6' },
                      { label: 'Cost per acquisition', value: '−38%', color: '#0072EE' },
                      { label: 'Showroom appts', value: '2.1×', color: '#FB6A48' },
                    ].map((m)=>(
                      <div key={m.label} className="flex items-center justify-between p-2.5 rounded-lg bg-bp-slate-50">
                        <span className="text-[13px] font-medium text-bp-slate">{m.label}</span>
                        <span className="font-display font-extrabold text-[20px] tracking-tight" style={{color:m.color}}>{m.value}</span>
                      </div>
                    ))}
                  </div>
                  {/* Linking flag */}
                  <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-bp-ink text-white px-4 py-2 rounded-full text-[12px] font-semibold whitespace-nowrap flex items-center gap-1.5">
                    <Sparkles size={13} className="text-bp-teal"/> The Partnership
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─────────────────── STATS BAND ─────────────────── */}
      <section className="py-10 lg:py-14 border-y border-bp-slate-200 bg-white" data-testid="stats-band">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
          {HEADLINE_STATS.map((s, i) => (
            <Reveal as="div" key={s.label} delay={i * 80} className="text-center lg:text-left">
              <div className="font-display font-extrabold text-[44px] sm:text-[56px] leading-none tracking-tight grad-text">
                {s.value}{s.suffix}
              </div>
              <div className="text-[13px] font-medium text-bp-slate-soft mt-2 uppercase tracking-wider">
                {s.label}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─────────────────── CLIENT MARQUEE ─────────────────── */}
      <section className="py-12 bg-bp-slate-50" data-testid="client-marquee">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 text-center">
          <p className="text-[12.5px] font-semibold tracking-wider uppercase text-bp-slate-soft">Trusted by industry leaders for nearly two decades</p>
        </div>
        <div className="bp-marquee mt-8 fade-edges">
          <div className="bp-marquee-track flex whitespace-nowrap">
            {[...CLIENT_BRANDS, ...CLIENT_BRANDS].map((b, i) => (
              <span key={i} className="inline-flex items-center gap-10 px-10 font-display font-bold text-[22px] sm:text-[26px] tracking-tight text-bp-slate-soft/70">
                {b}
                <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-bp-blue/30"/>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── FRESH WORK STRIP ─────────────────── */}
      {freshLaunches.length > 0 && (
        <section className="py-16 lg:py-24 bg-white" data-testid="fresh-work">
          <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
              <div className="max-w-2xl">
                <Reveal as="div" className="kicker">
                  <span className="dot"/>
                  <Sparkles size={12} className="text-bp-teal"/>
                  Freshly launched
                </Reveal>
                <Reveal as="h2" delay={80} className="h-display mt-4 text-[36px] sm:text-[48px]">
                  Our latest <span className="grad-text">client launches.</span>
                </Reveal>
                <Reveal as="p" delay={140} className="mt-4 text-[16px] lg:text-[17px] text-bp-slate leading-relaxed">
                  Three brand-new sites we just shipped. Take a peek at what shipping with us actually looks like.
                </Reveal>
              </div>
              <Reveal as="div" delay={200}>
                <Link
                  to="/portfolio?sort=newest"
                  className="inline-flex items-center gap-1.5 font-semibold text-bp-blue hover:text-bp-teal transition-colors"
                  data-testid="fresh-work-see-all"
                >
                  See all recent work <ArrowRight size={15}/>
                </Link>
              </Reveal>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {freshLaunches.map((p, i) => (
                <Reveal
                  as="article"
                  key={p.url}
                  delay={i * 100}
                  className="card-soft overflow-hidden group flex flex-col"
                  data-testid={`fresh-work-item-${i}`}
                >
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="block relative overflow-hidden">
                    <div className="aspect-[4/3] bg-bp-slate-50 overflow-hidden">
                      <img
                        src={p.img}
                        alt={`${p.name} - website preview`}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bp-teal/95 text-white text-[10.5px] font-bold uppercase tracking-[0.14em] shadow-sm backdrop-blur-sm ring-1 ring-white/25">
                      <span className="relative inline-flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-white/70 opacity-75 animate-ping"/>
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white"/>
                      </span>
                      New
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-t from-bp-ink/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-bp-ink text-[12px] font-semibold">
                        Visit site <ExternalLink size={12}/>
                      </span>
                    </div>
                  </a>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-bp-blue">{p.category}</div>
                    <h3 className="font-display font-bold text-bp-ink text-[18px] leading-tight mt-1.5">{p.name}</h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-bp-slate flex-1">{p.desc}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {p.services.map((s) => (
                        <span key={s} className="px-2 py-0.5 rounded-full bg-bp-slate-50 text-[11.5px] font-medium text-bp-slate">{s}</span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────── PARTNERSHIP PILLARS ─────────────────── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="pillars">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal as="div" className="kicker justify-center"><span className="dot"/>The partnership difference</Reveal>
            <Reveal as="h2" delay={80} className="h-display mt-4 text-[40px] sm:text-[54px]">
              We&rsquo;re not a vendor. <span className="grad-text">We&rsquo;re a partner.</span>
            </Reveal>
            <Reveal as="p" delay={140} className="mt-5 text-[17px] text-bp-slate leading-relaxed">
              Most agencies disappear after onboarding. We show up - in your inbox, on your calls,
              in your numbers - every week, every month, every year.
            </Reveal>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {PARTNERSHIP_PILLARS.map((p, i) => {
              const c = COLOR_MAP[p.color];
              return (
                <Reveal as="article" key={p.title} delay={i * 100} className="card-soft p-7 lg:p-8 group">
                  <div className={`w-14 h-14 rounded-2xl ${c.bg} ${c.text} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    {p.icon === 'handshake' && <Users size={26} />}
                    {p.icon === 'target' && <Target size={26} />}
                    {p.icon === 'heart' && <Sparkles size={26} />}
                  </div>
                  <h3 className="font-display font-bold text-[22px] text-bp-ink">{p.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-bp-slate">{p.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────────── SERVICES TABBED ─────────────────── */}
      <section className="py-20 lg:py-28 bg-bp-slate-50" data-testid="services-tabbed">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <Reveal as="div" className="kicker"><span className="dot"/>Everything under one roof</Reveal>
              <Reveal as="h2" delay={80} className="h-display mt-4 text-[40px] sm:text-[54px]">
                Full-service marketing, <span className="grad-text">no juggling.</span>
              </Reveal>
            </div>
            <Reveal as="p" delay={120} className="lg:col-span-5 text-[17px] text-bp-slate leading-relaxed">
              Strategy, design, development, advertising, content, analytics - all built in-house,
              all coordinated, all measured against the same goals.
            </Reveal>
          </div>

          {/* Tabs */}
          <div className="mt-12 flex flex-wrap gap-2.5" data-testid="services-tabs">
            {SERVICE_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`services-tab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
                className={`inline-flex items-center gap-2.5 px-5 py-3 rounded-full font-semibold text-[14.5px] transition-all ${
                  activeTab === t.key
                    ? 'bg-bp-ink text-white shadow-lg'
                    : 'bg-white border border-bp-slate-200 text-bp-slate hover:border-bp-blue hover:text-bp-blue'
                }`}
              >
                {SERVICE_ICONS[t.key]}
                {t.title}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="mt-10">
            <div className="card-floating p-7 lg:p-10">
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-bp-blue">{current.title}</div>
                  <h3 className="font-display font-bold text-[26px] text-bp-ink leading-tight mt-2">{current.blurb}</h3>
                  <Link to="/services" className="btn-ghost-blue mt-6">Full service list <ArrowRight size={14}/></Link>
                </div>
                <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
                  {current.items.map((item) => (
                    <div key={item.name} className="p-4 rounded-xl bg-bp-slate-50 hover:bg-bp-blue-50 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-display font-semibold text-bp-ink text-[15.5px] leading-tight">{item.name}</div>
                        <ArrowRight size={16} className="text-bp-slate-soft mt-1 flex-shrink-0"/>
                      </div>
                      <div className="text-[13.5px] text-bp-slate mt-1.5">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────── AUDIENCE TIERS - for every business ─────────────────── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="audience-tiers">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal as="div" className="kicker justify-center"><span className="dot" style={{background:'#FB6A48'}}/>For every business, big or small</Reveal>
            <Reveal as="h2" delay={80} className="h-display mt-4 text-[40px] sm:text-[54px]">
              Same care. <span className="grad-text">Same craft.</span> Different scale.
            </Reveal>
            <Reveal as="p" delay={140} className="mt-5 text-[17px] text-bp-slate">
              Whether you&rsquo;re running the only location in town or fifty across three states, we tailor the engagement to where you are.
            </Reveal>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {AUDIENCE_TIERS.map((a, i) => {
              const c = COLOR_MAP[a.color];
              return (
                <Reveal as="article" key={a.title} delay={i * 80} className="card-soft p-7 flex flex-col">
                  <div className={`inline-flex self-start items-center gap-1.5 px-3 py-1 rounded-full ${c.bg} ${c.text} text-[12px] font-semibold`}>
                    {a.badge}
                  </div>
                  <h3 className="font-display font-bold text-[24px] text-bp-ink mt-5">{a.title}</h3>
                  <div className="text-[13.5px] text-bp-slate-soft mt-1">{a.line}</div>
                  <p className="mt-4 text-[15px] leading-relaxed text-bp-slate">{a.body}</p>
                  <ul className="mt-6 space-y-2 flex-1">
                    {a.highlights.map((h) => (
                      <li key={h} className="flex items-center gap-2 text-[13.5px] text-bp-slate">
                        <span className={`w-1.5 h-1.5 rounded-full`} style={{background: c.solid}}/>
                        {h}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────────── RESULTS / CASES ─────────────────── */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-white to-bp-blue-50" data-testid="results">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <Reveal as="div" className="kicker"><span className="dot"/>The work, the numbers</Reveal>
              <Reveal as="h2" delay={80} className="h-display mt-3 text-[40px] sm:text-[54px]">
                Results <span className="grad-text">we love to brag about.</span>
              </Reveal>
            </div>
            <Link to="/portfolio" className="btn-secondary">See all work <ArrowRight size={16}/></Link>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {RESULT_CASES.map((r, i) => {
              const c = COLOR_MAP[r.color];
              return (
                <Reveal as="article" key={r.n} delay={i * 80} className="card-floating p-7 lg:p-8 group cursor-pointer">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-bp-slate-soft">{r.category}</div>
                      <h3 className="font-display font-bold text-[24px] text-bp-ink mt-2 leading-tight">{r.client}</h3>
                    </div>
                    <div className={`w-12 h-12 rounded-2xl ${c.bg} ${c.text} flex items-center justify-center group-hover:rotate-12 transition-transform`}>
                      <TrendingUp size={22}/>
                    </div>
                  </div>
                  <p className="mt-4 text-[15px] text-bp-slate leading-relaxed">{r.headline}</p>

                  <div className="mt-6 grid grid-cols-5 gap-4 items-end">
                    <div className="col-span-2">
                      <div className="font-display font-extrabold text-[44px] leading-none tracking-tight" style={{color: c.solid}}>{r.metric}</div>
                      <div className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft mt-2">{r.metricLabel}</div>
                    </div>
                    <div className="col-span-3 flex flex-wrap justify-end gap-2">
                      {r.services.map((s) => (
                        <span key={s} className="px-2.5 py-1 rounded-full bg-bp-slate-100 text-[12px] font-semibold text-bp-slate">{s}</span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────────── PROCESS ─────────────────── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="process">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <Reveal as="div" className="kicker"><span className="dot"/>How we work together</Reveal>
              <Reveal as="h2" delay={80} className="h-display mt-3 text-[40px] sm:text-[54px]">
                Four steps. <span className="grad-text">No mystery.</span>
              </Reveal>
            </div>
            <Reveal as="p" delay={120} className="lg:col-span-5 text-[17px] text-bp-slate leading-relaxed">
              A proven process refined over 19 years and 1,000+ launches. Built for transparency,
              speed, and outcomes you can actually see.
            </Reveal>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 relative">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal as="div" key={s.n} delay={i * 100} className="relative">
                <div className="card-soft p-6 lg:p-7 h-full">
                  <div className="font-display font-extrabold text-[44px] leading-none grad-text">{s.n}</div>
                  <h3 className="font-display font-bold text-[20px] text-bp-ink mt-5">{s.title}</h3>
                  <p className="text-[14.5px] text-bp-slate mt-2.5 leading-relaxed">{s.body}</p>
                </div>
                {i < PROCESS_STEPS.length - 1 && (
                  <ArrowRight size={22} className="hidden lg:block absolute top-1/2 -right-4 -translate-y-1/2 text-bp-blue/40"/>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── TESTIMONIALS ─────────────────── */}
      <section className="py-20 lg:py-28 bg-bp-slate-50" data-testid="testimonials">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <Reveal as="div" className="kicker"><span className="dot" style={{background:'#F59E0B'}}/>5.0 · 21 Google Reviews</Reveal>
              <Reveal as="h2" delay={80} className="h-display mt-3 text-[40px] sm:text-[54px]">
                What clients <span className="grad-text">actually say.</span>
              </Reveal>
            </div>
            <Reveal as="div" delay={120} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-bp-slate-200">
              <div className="flex">
                {[0,1,2,3,4].map((i)=><Star key={i} size={16} className="text-bp-amber fill-bp-amber"/>)}
              </div>
              <span className="text-[13px] font-semibold text-bp-slate">5.0 on Google</span>
            </Reveal>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <Reveal as="figure" key={t.author} delay={i * 80} className="card-soft p-6 flex flex-col">
                <Quote size={22} className="text-bp-blue/30"/>
                <blockquote className="mt-3 text-[14.5px] leading-relaxed text-bp-slate flex-1">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-5 pt-5 border-t border-bp-slate-200">
                  <div className="font-display font-bold text-bp-ink text-[15px]">{t.author}</div>
                  <div className="text-[12.5px] text-bp-slate-soft mt-0.5">{t.role}</div>
                </figcaption>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── FRESH ARTICLES STRIP ─────────────────── */}
      {freshArticles.length > 0 && (
        <section className="py-20 lg:py-28 bg-bp-slate-50" data-testid="fresh-articles">
          <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
              <div className="max-w-2xl">
                <Reveal as="div" className="kicker">
                  <span className="dot"/>
                  <Sparkles size={12} className="text-bp-teal"/>
                  Fresh thinking
                </Reveal>
                <Reveal as="h2" delay={80} className="h-display mt-4 text-[36px] sm:text-[48px]">
                  Latest from the <span className="grad-text">bp. journal.</span>
                </Reveal>
                <Reveal as="p" delay={140} className="mt-4 text-[16px] lg:text-[17px] text-bp-slate leading-relaxed">
                  Client launches, Google updates, and partnership stories from the field - straight from our desks to yours.
                </Reveal>
              </div>
              <Reveal as="div" delay={200}>
                <Link
                  to="/articles?sort=newest"
                  className="inline-flex items-center gap-1.5 font-semibold text-bp-blue hover:text-bp-teal transition-colors"
                  data-testid="fresh-articles-see-all"
                >
                  Read all articles <ArrowRight size={15}/>
                </Link>
              </Reveal>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {freshArticles.map((a, i) => (
                <Reveal
                  as="article"
                  key={a.slug}
                  delay={i * 100}
                  className="card-soft overflow-hidden group flex flex-col"
                  data-testid={`fresh-article-item-${i}`}
                >
                  <Link to={`/blog/${a.slug}`} className="block relative overflow-hidden">
                    <div className="aspect-[4/3] bg-bp-slate-50 overflow-hidden">
                      <img
                        src={a.img}
                        alt={a.title}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-bp-ink/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-bp-ink text-[12px] font-semibold">
                        Read article <ArrowRight size={12}/>
                      </span>
                    </div>
                  </Link>
                  <Link to={`/blog/${a.slug}`} className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider">
                      <span className="text-bp-blue">{a.category}</span>
                      <span className="w-1 h-1 rounded-full bg-bp-slate-soft"/>
                      <span className="text-bp-slate-soft normal-case tracking-normal text-[12px] font-medium">{a.date}</span>
                    </div>
                    <h3 className="font-display font-bold text-bp-ink text-[18px] leading-tight mt-2 group-hover:text-bp-blue transition-colors">{a.title}</h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-bp-slate flex-1 line-clamp-3">{a.excerpt}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────── FINAL CTA ─────────────────── */}
      <section className="py-20 lg:py-28" data-testid="final-cta">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="rounded-3xl relative overflow-hidden p-10 lg:p-16 text-white shadow-2xl" style={{background: 'linear-gradient(135deg, #0072EE 0%, #0072EE 50%, #14B8A6 100%)'}}>
            <div className="absolute -right-10 -bottom-10 w-72 h-72 rounded-full bg-white/10 blur-3xl"/>
            <div className="absolute -left-10 -top-10 w-72 h-72 rounded-full bg-white/10 blur-3xl"/>
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="text-[12px] font-semibold uppercase tracking-wider text-white/80">Your move</div>
                <h2 className="font-display font-extrabold text-[40px] sm:text-[58px] leading-[1.02] tracking-tight mt-3">
                  Let&rsquo;s find your next 100 customers.
                </h2>
                <p className="mt-5 text-[17px] text-white/85 max-w-xl">
                  Book a free, no-pressure strategy call. We&rsquo;ll audit your marketing, share what we&rsquo;d do differently,
                  and you decide what comes next.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-bp-blue font-bold shadow-lg hover:bg-bp-blue-50 transition-colors" data-testid="final-cta-primary">
                  <Phone size={16}/> Book Your Strategy Call
                </Link>
                <a href="tel:+19547459545" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white/15 text-white font-semibold hover:bg-white/20 transition-colors" data-testid="final-cta-phone">
                  Or call 954-745-9545
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
